import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Eye, EyeOff, Globe2, MonitorSmartphone, Plus, RefreshCw, ShieldCheck, ShieldOff, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import { IconAvatar } from "@/components/app/IconAvatar";
import {
  API_BASE_URL, connectShieldDevice, deleteShieldRule, disconnectShieldDevice, getPartnerShield, getShield, saveShieldRule, setShieldShare,
} from "@/lib/api";
import { SHIELD_DOWNLOAD, SHIELD_STORE_URL, askShield, browserLabel, duration, localDay, onShieldReady } from "@/lib/shield";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SHARE_LEVELS = [
  ["none", "Nothing", "They cannot see your Shield and are not told when you add time."],
  ["listed", "My list", "Time and blocks on the sites you listed, and any extra time you take."],
  ["all", "Everything", "The above, plus time on every other site."],
];

const relative = (value) => {
  if (!value) return "never";
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} d ago`;
};

const limitSummary = (rule) => {
  if (rule.mode === "block") return "Blocked every day";
  const set = DAYS.filter((day) => rule.limits[day] != null);
  if (!set.length) return "No limit set";
  const values = new Set(set.map((day) => rule.limits[day]));
  if (set.length === 7 && values.size === 1) return `${rule.limits.Mon} min a day`;
  return set.map((day) => `${day} ${rule.limits[day]}m`).join(", ");
};

export default function Shield() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [extension, setExtension] = useState(null);
  const [busy, setBusy] = useState(false);
  const day = localDay();

  const load = useCallback(() => getShield(day).then((result) => { setData(result); setError(null); }).catch((caught) => setError(caught.message)), [day]);
  const detect = useCallback(() => askShield("status").then(setExtension), []);

  useEffect(() => {
    load();
    detect();
    const stop = onShieldReady(detect);
    const timer = setInterval(load, 60000);
    return () => { stop(); clearInterval(timer); };
  }, [load, detect]);

  const guarded = async (work, failure) => {
    setBusy(true);
    try { await work(); } catch (caught) { toast(failure, { description: caught.message }); } finally { setBusy(false); }
  };

  const devices = data?.devices || [];
  const thisDevice = extension?.connected ? devices.find((device) => device.id === extension.deviceId) : null;
  const siteRules = (data?.rules || []).filter((rule) => rule.kind !== "app");

  const connect = () => guarded(async () => {
    const device = await connectShieldDevice({ label: browserLabel(), version: extension?.version });
    const reply = await askShield("connect", { token: device.token, apiBase: API_BASE_URL, deviceId: device.deviceId, name: device.name }, 15000);
    if (!reply.ok) { await disconnectShieldDevice(device.deviceId).catch(() => {}); throw new Error(reply.error || "The extension did not answer."); }
    setExtension(reply);
    toast("This browser is connected.", { description: "Your Shield is counting and blocking here now." });
    await load();
  }, "Could not connect this browser.");

  const disconnect = (device) => guarded(async () => {
    if (!window.confirm(`Disconnect ${device.label}? It stops blocking and counting, and any partner you share with is told.`)) return;
    await disconnectShieldDevice(device.id);
    if (device.id === extension?.deviceId) setExtension(await askShield("disconnect"));
    await load();
  }, "Could not disconnect that browser.");

  const refreshExtension = async () => { await askShield("refresh", {}, 15000); await load(); };

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10" data-testid="shield-page">
        <div className="mb-8">
          <div className="overline mb-3">Distraction shield</div>
          <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05]">Keep the day <em className="italic text-[#F59E0B]">yours.</em></h1>
          <p className="mt-3 max-w-2xl font-light text-[#A1A1AA]">Choose the sites that eat your time. The Shield blocks them or caps them in your browser, counts time per site, and lets a partner see how it went. It records the site only, such as youtube.com, never the page you opened or what you searched for. <Link to="/shield/about" className="text-[#F59E0B] underline-offset-4 hover:underline">How the Shield works</Link> · App Shield for phones is coming soon.</p>
        </div>

        {error && !data && <div className="hm-card rounded-md p-6 text-sm text-[#A1A1AA]">Could not load your Shield. {error} <button type="button" onClick={load} className="ml-2 text-[#F59E0B] hover:underline">Try again</button></div>}

        {data && (
          <div className="grid gap-6">
            <BrowserCard extension={extension} devices={devices} thisDevice={thisDevice} busy={busy} onConnect={connect} onDisconnect={disconnect} onDetect={detect} />
            <RulesCard kind="site" rules={siteRules} busy={busy} onChanged={async () => { await load(); refreshExtension(); }} guarded={guarded} hasPartners={data.partners.some((partner) => partner.myLevel !== "none")} />
            <AppShieldSoon />
            <UsageCard title="Today" summary={data} own />
            <PartnersCard partners={data.partners} day={day} guarded={guarded} onChanged={load} openId={params.get("partner")} onOpen={(id) => setParams(id ? { partner: id } : {})} />
          </div>
        )}
      </div>
    </AppShell>
  );
}

// The Android app isn't public yet, so on the web the phone half of the Shield is announced, not offered.
function AppShieldSoon() {
  return (
    <section className="hm-card flex flex-col gap-4 rounded-md p-6 sm:flex-row sm:items-center sm:p-8" data-testid="shield-apps-soon">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-white/10 text-[#A1A1AA]"><Smartphone className="h-5 w-5" strokeWidth={1.5} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-serif-editorial text-2xl">App Shield for your phone</h2>
          <span className="rounded-full border border-[#F59E0B]/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F59E0B]">Coming soon</span>
        </div>
        <p className="mt-1 max-w-2xl text-sm font-light text-[#A1A1AA]">Blocking and limiting phone apps arrives with the HustlrMate Android app. Until then the Shield covers websites on your computer, and your partner sees those.</p>
      </div>
    </section>
  );
}

function BrowserCard({ extension, devices, thisDevice, busy, onConnect, onDisconnect, onDetect }) {
  const installed = Boolean(extension?.installed);
  // Chrome Web Store rules: say what is collected, and get a clear yes, before the extension starts.
  const [agreed, setAgreed] = useState(false);
  return (
    <section className="hm-card rounded-md p-6 sm:p-8" data-testid="shield-browser">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-4">
          <span className={`mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border ${thisDevice ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-white/10 text-[#A1A1AA]"}`}>
            {thisDevice ? <ShieldCheck className="h-5 w-5" strokeWidth={1.5} /> : <ShieldOff className="h-5 w-5" strokeWidth={1.5} />}
          </span>
          <div>
            <h2 className="font-serif-editorial text-2xl">{thisDevice ? "This browser is protected." : installed ? "The extension is installed. Connect it." : "Add the Shield to this browser."}</h2>
            <p className="mt-1 max-w-xl text-sm font-light text-[#A1A1AA]">
              {thisDevice
                ? `Connected as ${thisDevice.label}. It reports to HustlrMate every five minutes.`
                : installed
                  ? "Connecting links this browser to your account. Until then it counts and blocks nothing."
                  : "The Shield is a browser extension for Chrome, Edge and other Chromium browsers on a computer. A website cannot block other sites by itself."}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {!installed && (SHIELD_STORE_URL
            ? <a href={SHIELD_STORE_URL} target="_blank" rel="noreferrer" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold"><Download className="h-4 w-4" /> Add to browser</a>
            : <a href={SHIELD_DOWNLOAD} download className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold" data-testid="shield-download"><Download className="h-4 w-4" /> Download the extension</a>)}
          {!installed && <button type="button" onClick={onDetect} className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm"><RefreshCw className="h-4 w-4" /> I've installed it</button>}
          {installed && !thisDevice && <button type="button" onClick={onConnect} disabled={busy || !agreed} className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50" data-testid="shield-connect"><ShieldCheck className="h-4 w-4" /> Connect this browser</button>}
        </div>
      </div>

      {installed && !thisDevice && (
        <label className="mt-6 flex cursor-pointer gap-3 border-t border-white/5 pt-5 text-sm font-light text-[#A1A1AA]" data-testid="shield-consent">
          <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#F59E0B]" />
          <span>
            I agree that, once connected, the Shield records the <span className="text-[#F5F5F0]">name of each site</span> I visit in this browser (never the page or what I search), the time I spend on it and how often it was opened or blocked, and sends those daily totals to HustlrMate to enforce my limits and show me and the partners I choose. <Link to="/shield/about" className="text-[#F59E0B] underline-offset-4 hover:underline">What the Shield collects</Link>
          </span>
        </label>
      )}

      {!installed && (
        <ol className="mt-6 grid gap-2 border-t border-white/5 pt-5 text-sm font-light text-[#A1A1AA] sm:grid-cols-2">
          {SHIELD_STORE_URL ? <>
            <li><span className="text-[#F5F5F0]">1.</span> Press "Add to browser". The extension's store page opens.</li>
            <li><span className="text-[#F5F5F0]">2.</span> Choose <span className="text-[#F5F5F0]">Add to Chrome</span> (or Get, in Edge) and confirm.</li>
            <li><span className="text-[#F5F5F0]">3.</span> Come back to this page and press "I've installed it".</li>
            <li><span className="text-[#F5F5F0]">4.</span> Agree to what it records, then press Connect this browser.</li>
          </> : <>
            <li><span className="text-[#F5F5F0]">1.</span> Download the file above and unzip it somewhere you will keep it.</li>
            <li><span className="text-[#F5F5F0]">2.</span> Open <span className="text-[#F5F5F0]">chrome://extensions</span> (or edge://extensions) and switch on Developer mode.</li>
            <li><span className="text-[#F5F5F0]">3.</span> Choose Load unpacked and pick the unzipped folder.</li>
            <li><span className="text-[#F5F5F0]">4.</span> Come back to this page, press "I've installed it", then connect.</li>
          </>}
          <li className="sm:col-span-2 text-xs"><Link to="/shield/about#get-it" className="text-[#F59E0B] underline-offset-4 hover:underline">Full install guide</Link> · Works in Chrome, Edge, Brave and other Chromium browsers on a computer.</li>
        </ol>
      )}

      {devices.length > 0 && (
        <ul className="mt-6 divide-y divide-white/5 border-t border-white/5">
          {devices.map((device) => (
            <li key={device.id} className="flex items-center gap-3 py-3 text-sm">
              {device.kind === "android" ? <Smartphone className="h-4 w-4 shrink-0 text-[#A1A1AA]" strokeWidth={1.5} /> : <MonitorSmartphone className="h-4 w-4 shrink-0 text-[#A1A1AA]" strokeWidth={1.5} />}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[#F5F5F0]">{device.label}{device.id === thisDevice?.id ? " (this browser)" : ""}</div>
                <div className="text-xs text-[#8a8a92]">{device.active ? "Active now" : `Last reported ${relative(device.lastSeenAt)}`}</div>
              </div>
              <span className={`h-2 w-2 shrink-0 rounded-full ${device.active ? "bg-emerald-400" : "bg-white/20"}`} aria-hidden />
              <button type="button" onClick={() => onDisconnect(device)} disabled={busy} className="text-xs text-[#A1A1AA] hover:text-[#F5F5F0] disabled:opacity-50">Disconnect</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Websites are added here by typing them. Phone apps are added from the Android app, which knows
// what is installed; here they can be changed or removed.
function RulesCard({ kind, rules, busy, onChanged, guarded, hasPartners }) {
  const apps = kind === "app";
  const blank = { kind, domain: "", label: "", mode: "block", limits: Object.fromEntries(DAYS.map((day) => [day, "30"])) };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(false);

  const submit = (event) => {
    event.preventDefault();
    guarded(async () => {
      const limits = form.mode === "limit" ? Object.fromEntries(DAYS.filter((day) => form.limits[day] !== "").map((day) => [day, Math.max(0, Math.min(1440, Number(form.limits[day]) || 0))])) : {};
      await saveShieldRule({ kind, domain: form.domain, label: form.label || undefined, mode: form.mode, limits });
      setForm(blank);
      setEditing(false);
      await onChanged();
    }, apps ? "Could not save that app." : "Could not save that site.");
  };

  const edit = (rule) => {
    setForm({ kind, domain: rule.domain, label: rule.label, mode: rule.mode, limits: Object.fromEntries(DAYS.map((day) => [day, rule.limits[day] != null ? String(rule.limits[day]) : rule.mode === "limit" ? "" : "30"])) });
    setEditing(true);
  };

  const remove = (rule) => guarded(async () => {
    if (!window.confirm(`Take ${rule.label || rule.domain} off your Shield?${hasPartners ? " The partners you share with are told." : ""}`)) return;
    await deleteShieldRule(rule.id);
    await onChanged();
  }, apps ? "Could not remove that app." : "Could not remove that site.");

  return (
    <section className="hm-card rounded-md p-6 sm:p-8" data-testid={apps ? "shield-app-rules" : "shield-rules"}>
      <h2 className="font-serif-editorial text-2xl">{apps ? "Apps on your Shield" : "Sites on your Shield"}</h2>
      <p className="mt-1 text-sm font-light text-[#A1A1AA]">
        {apps
          ? "Blocked or limited on your Android phone. Add apps from the HustlrMate app, which can see what is installed; change or remove them here."
          : "Block a site outright, or give it a daily allowance that can differ by weekday. A rule covers the whole site, including its subdomains."}
      </p>

      {(!apps || editing) && <form onSubmit={submit} className="mt-5 grid gap-4 rounded-md border border-white/5 bg-white/[0.02] p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="grid gap-1 text-xs text-[#A1A1AA]">{apps ? "App" : "Site"}
            <input value={apps ? form.label || form.domain : form.domain} onChange={(event) => setForm({ ...form, domain: event.target.value })} placeholder="youtube.com" required disabled={editing} className="hm-input rounded-md px-3 py-2 text-sm disabled:opacity-60" data-testid="shield-domain" />
          </label>
          <fieldset className="grid gap-1 text-xs text-[#A1A1AA]">
            <legend className="mb-1">What should happen</legend>
            <div className="flex gap-2">
              {[["block", "Block it"], ["limit", "Limit it"]].map(([value, label]) => (
                <label key={value} className={`cursor-pointer rounded-md border px-3 py-2 text-sm ${form.mode === value ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 text-[#A1A1AA]"}`}>
                  <input type="radio" name={`mode-${kind}`} value={value} checked={form.mode === value} onChange={() => setForm({ ...form, mode: value })} className="sr-only" />{label}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        {form.mode === "limit" && (
          <div>
            <div className="mb-2 text-xs text-[#A1A1AA]">Minutes allowed each day. Leave a day empty for no limit; 0 blocks it that day.</div>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
              {DAYS.map((day) => (
                <label key={day} className="grid gap-1 text-xs text-[#A1A1AA]">{day}
                  <input type="number" min="0" max="1440" inputMode="numeric" value={form.limits[day]} onChange={(event) => setForm({ ...form, limits: { ...form.limits, [day]: event.target.value } })} className="hm-input w-full rounded-md px-2 py-2 text-sm" data-testid={`shield-limit-${day}`} />
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <button type="submit" disabled={busy} className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50" data-testid="shield-save-rule"><Plus className="h-4 w-4" /> {editing ? "Save changes" : "Add to Shield"}</button>
          {editing && <button type="button" onClick={() => { setForm(blank); setEditing(false); }} className="hm-btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>}
        </div>
      </form>}

      {rules.length === 0
        ? <p className="mt-5 text-sm font-light italic text-[#8a8a92]">{apps ? "No apps listed yet. Add one from the Shield screen in the Android app." : "Nothing listed yet. Start with the one site you open without meaning to."}</p>
        : (
          <ul className="mt-5 divide-y divide-white/5">
            {rules.map((rule) => (
              <li key={rule.id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[#F5F5F0]">{rule.label || rule.domain}</div>
                  <div className="text-xs text-[#8a8a92]">{limitSummary(rule)}</div>
                </div>
                <button type="button" onClick={() => edit(rule)} className="text-xs text-[#A1A1AA] hover:text-[#F5F5F0]">Edit</button>
                <button type="button" onClick={() => remove(rule)} disabled={busy} aria-label={`Remove ${rule.label || rule.domain}`} className="rounded-md p-2 text-[#A1A1AA] hover:bg-white/[0.06] hover:text-[#F5F5F0] disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

// One person's day: used by your own Shield and by the view of a partner's.
function UsageCard({ title, summary, own = false }) {
  const [everything, setEverything] = useState(false);
  const canSeeAll = summary.scope === "all";
  const rows = summary.today.filter((row) => everything || row.listed);
  const listedSeconds = summary.today.filter((row) => row.listed).reduce((sum, row) => sum + row.seconds, 0);
  const blocked = summary.today.reduce((sum, row) => sum + row.blocked, 0);
  const extra = summary.overrides.filter((item) => item.day === summary.day);
  const peak = Math.max(1, ...summary.week.map((item) => (canSeeAll ? item.seconds : item.listedSeconds)));

  return (
    <section className={own ? "hm-card rounded-md p-6 sm:p-8" : ""} data-testid={own ? "shield-today" : "shield-partner-view"}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif-editorial text-2xl">{title}</h2>
          <p className="mt-1 text-sm font-light text-[#A1A1AA]">{duration(listedSeconds)} on your list. Blocked {blocked} {blocked === 1 ? "time" : "times"}. Extra time taken {extra.length} {extra.length === 1 ? "time" : "times"}.</p>
        </div>
        {canSeeAll && (
          <button type="button" onClick={() => setEverything((value) => !value)} className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs" aria-pressed={everything}>
            {everything ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {everything ? "Your list only" : "Show everything"}
          </button>
        )}
      </div>

      {rows.length === 0
        ? <p className="mt-5 text-sm font-light italic text-[#8a8a92]">{summary.devices.length ? "Nothing recorded yet today." : own ? "Connect a browser to start counting." : "No browser is connected."}</p>
        : (
          <ul className="mt-5 grid gap-3">
            {rows.map((row) => {
              const allowed = row.allowedMinutes == null ? null : row.allowedMinutes * 60;
              const share = allowed ? Math.min(1, row.seconds / allowed) : row.listed && allowed === 0 ? (row.seconds ? 1 : 0) : 0;
              const over = allowed != null && row.seconds >= allowed && (allowed > 0 || row.blocked > 0);
              return (
                <li key={`${row.kind}:${row.domain}`}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className={`flex min-w-0 items-center gap-2 ${row.listed ? "text-[#F5F5F0]" : "text-[#A1A1AA]"}`}>
                      {row.kind === "app" ? <Smartphone className="h-3.5 w-3.5 shrink-0 text-[#8a8a92]" aria-label="App" /> : <Globe2 className="h-3.5 w-3.5 shrink-0 text-[#8a8a92]" aria-label="Website" />}
                      <span className="truncate">{row.label || row.domain}</span>
                    </span>
                    <span className="shrink-0 text-xs text-[#A1A1AA] tabular-nums">
                      {allowed === 0
                        ? `Blocked${row.blocked ? `, stopped ${row.blocked}x` : ""}${row.seconds ? `, ${duration(row.seconds)} with extra time` : ""}`
                        : `${duration(row.seconds)}${allowed != null ? ` of ${duration(allowed)}` : ""}${row.blocked ? `, blocked ${row.blocked}x` : ""}`}
                    </span>
                  </div>
                  {row.listed && allowed !== 0 && allowed != null && <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/[0.06]"><div className={`h-full ${over ? "bg-red-300" : "bg-[#F59E0B]"}`} style={{ width: `${share * 100}%` }} /></div>}
                </li>
              );
            })}
          </ul>
        )}

      <div className="mt-6 grid gap-6 border-t border-white/5 pt-5 md:grid-cols-2">
        <div>
          <div className="mb-3 text-xs text-[#A1A1AA]">Last seven days{canSeeAll ? ", everything" : ", your list"}</div>
          <div className="flex h-24 items-end gap-2" role="img" aria-label={summary.week.map((item) => `${item.day}: ${duration(canSeeAll ? item.seconds : item.listedSeconds)}`).join(". ")}>
            {summary.week.map((item) => {
              const value = canSeeAll ? item.seconds : item.listedSeconds;
              return (
                <div key={item.day} className="flex h-full flex-1 flex-col items-center justify-end gap-1" title={`${item.day}: ${duration(value)}, blocked ${item.blocked}x`}>
                  <div className={`w-full rounded-sm ${item.day === summary.day ? "bg-[#F59E0B]" : "bg-white/20"}`} style={{ height: `${Math.max(2, (value / peak) * 100)}%` }} />
                  <span className="text-[10px] text-[#8a8a92]">{new Date(`${item.day}T12:00:00`).toLocaleDateString(undefined, { weekday: "narrow" })}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div className="mb-3 text-xs text-[#A1A1AA]">Extra time taken this week</div>
          {summary.overrides.length === 0
            ? <p className="text-sm font-light italic text-[#8a8a92]">None. Every block held.</p>
            : (
              <ul className="grid gap-2 text-sm">
                {summary.overrides.slice(0, 6).map((item) => (
                  <li key={item.id} className="flex justify-between gap-3">
                    <span className="min-w-0 truncate text-[#F5F5F0]">{item.label || item.domain} <span className="text-[#A1A1AA]">+{item.minutes} min{item.reason ? `, "${item.reason}"` : ""}</span></span>
                    <span className="shrink-0 text-xs text-[#8a8a92]">{relative(item.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </section>
  );
}

function PartnersCard({ partners, day, guarded, onChanged, openId, onOpen }) {
  const [views, setViews] = useState({});

  useEffect(() => {
    if (!openId || views[openId]) return;
    getPartnerShield(openId, day).then((result) => setViews((current) => ({ ...current, [openId]: result }))).catch((caught) => toast("Could not load their Shield.", { description: caught.message }));
  }, [openId, day, views]);

  if (partners.length === 0) {
    return (
      <section className="hm-card rounded-md p-6 sm:p-8">
        <h2 className="font-serif-editorial text-2xl">Partners</h2>
        <p className="mt-1 text-sm font-light text-[#A1A1AA]">When you are in a partnership, you can each choose to show the other your Shield. Until then it is yours alone.</p>
      </section>
    );
  }

  return (
    <section className="hm-card rounded-md p-6 sm:p-8" data-testid="shield-partners">
      <h2 className="font-serif-editorial text-2xl">Partners</h2>
      <p className="mt-1 text-sm font-light text-[#A1A1AA]">Each of you decides what the other sees. Sharing also means they are told when you give yourself extra time, remove a site, or disconnect.</p>
      <ul className="mt-5 grid gap-5">
        {partners.map((partner) => {
          const first = partner.name.split(" ")[0];
          const view = views[partner.partnershipId];
          const open = openId === partner.partnershipId;
          return (
            <li key={partner.partnershipId} className="rounded-md border border-white/5 bg-white/[0.02] p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <IconAvatar iconKey={partner.profileIcon} size="md" tone="outline" />
                  <div>
                    <div className="text-[#F5F5F0]">{partner.name}</div>
                    <div className="text-xs text-[#8a8a92]">{partner.theirLevel === "none" ? `${first} has not shared their Shield with you.` : partner.theirLevel === "all" ? `${first} shares everything with you.` : `${first} shares their list with you.`}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-[#A1A1AA]">{first} can see
                    <select
                      value={partner.myLevel}
                      onChange={(event) => guarded(async () => { await setShieldShare(partner.partnershipId, event.target.value); await onChanged(); }, "Could not change what you share.")}
                      className="hm-input rounded-md px-2 py-2 text-sm"
                      data-testid={`shield-share-${partner.partnershipId}`}
                    >
                      {SHARE_LEVELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  {partner.theirLevel !== "none" && (
                    <button type="button" onClick={() => onOpen(open ? null : partner.partnershipId)} className="hm-btn-ghost rounded-md px-3 py-2 text-xs" aria-expanded={open} data-testid={`shield-view-${partner.partnershipId}`}>
                      {open ? "Hide their Shield" : `See ${first}'s Shield`}
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-3 text-xs text-[#8a8a92]">{SHARE_LEVELS.find(([value]) => value === partner.myLevel)?.[2]}</p>
              {open && view?.shared && (
                <div className="mt-5 border-t border-white/5 pt-5">
                  <div className="mb-4 flex items-center gap-2 text-xs text-[#A1A1AA]">
                    <span className={`h-2 w-2 rounded-full ${view.active ? "bg-emerald-400" : "bg-white/20"}`} aria-hidden />
                    {view.devices.length === 0 ? `${first} has no browser or phone connected, so nothing is being blocked or counted.` : view.active ? `${first}'s Shield is on.` : `${first}'s Shield last reported ${relative(view.devices.map((device) => device.lastSeenAt).sort().pop())}.`}
                  </div>
                  <UsageCard title={`${first} today`} summary={view} />
                </div>
              )}
              {open && view && !view.shared && <p className="mt-4 text-sm text-[#A1A1AA]">{first} is no longer sharing their Shield.</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
